import { LIB } from './library';
import { symbolName } from './custom';
import type { Project, Sym } from './types';
import { t as $t, tk } from './i18n';


/*
 * Compares two versions of a project. Symbols, wires and pages are matched by id,
 * so moves and edits are recognised as such rather than as delete + add.
 */

export type ChangeKind = 'added' | 'removed' | 'changed' | 'moved';

export interface Change {
  kind: ChangeKind;
  /** page index in the newer version (or the older one for removed pages/objects) */
  pi: number;
  page: string;
  what: string;
  detail: string;
  /** symbol id in the newer version, for highlighting and jumping */
  symId?: string;
  wireId?: string;
}

export interface ProjectDiff {
  changes: Change[];
  /** ids (symbols and wires) changed in the newer version, per page id */
  touched: Map<string, Set<string>>;
  summary: { added: number; removed: number; changed: number; moved: number };
}

const symName = (s: Sym) => (s.tag ? `-${s.tag}` : symbolName(s.type)) + (s.tag && LIB[s.type] ? ` (${symbolName(s.type)})` : '');

const FIELDS: [keyof Sym | string, string][] = [
  ['tag', tk('etiket')], ['desc', tk("açıklama")], ['part', tk("parça no")], ['rot', tk("yön")], ['type', tk('sembol')], ['fn', '='], ['loc', '+'],
];

function propsDiff(a: Sym, b: Sym): string[] {
  const out: string[] = [];
  for (const [k, label] of FIELDS) {
    const va = (a as any)[k] ?? '', vb = (b as any)[k] ?? '';
    if (String(va) !== String(vb)) out.push(`${$t(label)}: ${va === '' ? '—' : va} → ${vb === '' ? '—' : vb}`);
  }
  const keys = new Set([...Object.keys(a.props ?? {}), ...Object.keys(b.props ?? {})]);
  for (const k of keys) {
    const va = (a.props as any)?.[k] ?? '', vb = (b.props as any)?.[k] ?? '';
    if (String(va) !== String(vb)) out.push(`${k}: ${va === '' ? '—' : va} → ${vb === '' ? '—' : vb}`);
  }
  return out;
}

export function diffProjects(older: Project, newer: Project): ProjectDiff {
  const changes: Change[] = [];
  const touched = new Map<string, Set<string>>();
  const touch = (pageId: string, id: string) => (touched.get(pageId) ?? touched.set(pageId, new Set()).get(pageId)!).add(id);
  const oldPages = new Map(older.pages.map((p, i) => [p.id, { p, i }]));
  const newIds = new Set(newer.pages.map((p) => p.id));

  const pf: [keyof Project, string][] = [['name', $t("proje adı")], ['customer', $t("müşteri")], ['fn', '='], ['loc', '+'], ['author', $t("çizen")]];
  const pd = pf.filter(([k]) => String(older[k] ?? '') !== String(newer[k] ?? '')).map(([k, l]) => `${l}: ${older[k] || '—'} → ${newer[k] || '—'}`);
  if (pd.length) changes.push({ kind: 'changed', pi: 0, page: 'Proje', what: $t("Proje bilgileri"), detail: pd.join(' · ') });

  newer.pages.forEach((np, pi) => {
    const old = oldPages.get(np.id);
    const page = `${pi + 1} · ${np.desc || $t("Adsız sayfa")}`;
    if (!old) {
      changes.push({ kind: 'added', pi, page, what: np.kind === 'layout' ? $t("Pano yerleşimi sayfası") : 'Sayfa', detail: $t("{0} sembol, {1} bağlantı", np.syms.length, np.wires.length) });
      np.syms.forEach((s) => touch(np.id, s.id));
      return;
    }
    const op = old.p;
    if ((op.desc || '') !== (np.desc || '')) changes.push({ kind: 'changed', pi, page, what: $t("Sayfa açıklaması"), detail: `${op.desc || '—'} → ${np.desc || '—'}` });
    if (old.i !== pi) changes.push({ kind: 'moved', pi, page, what: $t("Sayfa sırası"), detail: $t("{0}. sıradan {1}. sıraya", old.i + 1, pi + 1) });

    const os = new Map(op.syms.map((s) => [s.id, s]));
    const ns = new Set(np.syms.map((s) => s.id));
    for (const s of np.syms) {
      const o = os.get(s.id);
      if (!o) {
        changes.push({ kind: 'added', pi, page, what: symName(s), detail: `X ${s.x} Y ${s.y}`, symId: s.id });
        touch(np.id, s.id);
        continue;
      }
      const d = propsDiff(o, s);
      if (d.length) {
        changes.push({ kind: 'changed', pi, page, what: symName(s), detail: d.join(' · '), symId: s.id });
        touch(np.id, s.id);
      } else if (o.x !== s.x || o.y !== s.y) {
        changes.push({ kind: 'moved', pi, page, what: symName(s), detail: `(${o.x}, ${o.y}) → (${s.x}, ${s.y})`, symId: s.id });
        touch(np.id, s.id);
      }
    }
    for (const o of op.syms) if (!ns.has(o.id)) changes.push({ kind: 'removed', pi, page, what: symName(o), detail: `X ${o.x} Y ${o.y}` });

    const ow = new Map(op.wires.map((w) => [w.id, w]));
    const nw = new Set(np.wires.map((w) => w.id));
    let wAdd = 0, wDel = 0, wChg = 0;
    for (const w of np.wires) {
      const o = ow.get(w.id);
      if (!o) {
        wAdd++;
        touch(np.id, w.id);
      } else if (JSON.stringify(o.pts) !== JSON.stringify(w.pts) || o.cs !== w.cs || o.col !== w.col || (o.no ?? '') !== (w.no ?? '')) {
        wChg++;
        touch(np.id, w.id);
      }
    }
    for (const o of op.wires) if (!nw.has(o.id)) wDel++;
    if (wAdd) changes.push({ kind: 'added', pi, page, what: $t("Bağlantı"), detail: $t("{0} yeni bağlantı", wAdd) });
    if (wDel) changes.push({ kind: 'removed', pi, page, what: $t("Bağlantı"), detail: $t("{0} bağlantı silindi", wDel) });
    if (wChg) changes.push({ kind: 'changed', pi, page, what: $t("Bağlantı"), detail: $t("{0} bağlantının yolu ya da özelliği değişti", wChg) });

    if (op.layout || np.layout) {
      const a = op.layout, b = np.layout;
      const n = (l?: typeof a) => (l ? $t("{0} cihaz, {1} ray, {2} kanal, {3}×{4} mm", l.items.length, l.rails.length, l.ducts.length, l.w, l.h) : '—');
      if (JSON.stringify(a) !== JSON.stringify(b)) changes.push({ kind: 'changed', pi, page, what: $t("Pano yerleşimi"), detail: `${n(a)} → ${n(b)}` });
    }
  });
  older.pages.forEach((op, i) => {
    if (!newIds.has(op.id)) changes.push({ kind: 'removed', pi: i, page: `${i + 1} · ${op.desc || $t("Adsız sayfa")}`, what: 'Sayfa', detail: $t("{0} sembol, {1} bağlantı", op.syms.length, op.wires.length) });
  });
  const summary = { added: 0, removed: 0, changed: 0, moved: 0 };
  for (const c of changes) summary[c.kind]++;
  return { changes, touched, summary };
}
